import React, {useContext, useEffect, useState} from 'react';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Chip, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {AuthContext} from "../../auth/providers/AuthProvider";
import {CsrfContext} from "../../../providers/CsrfProvider";
import { listUserGroups, createGroup, updateGroup, archiveGroup, setGroupFavorite } from "../../../services/group/Groups";
import {ThemeContext} from "styled-components";
import InputValidatorField from "./Inputs/InputValidatorField";
import {IsValidName} from "../../../utils/Validators";
import {useGroup} from "../../../providers/GroupProvider";
import TableButton from "../../template/components/TableButton";
import AddIcon from '@mui/icons-material/Add';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import WithActions from "./Wrappers/WithActions";

const GroupList = () => {
    const { authClaims } = useContext(AuthContext);
    const { csrfToken } = useContext(CsrfContext);
    const theme = useContext(ThemeContext);
    const { groups, loading, refreshGroups, favoriteGroupId } = useGroup();
    // groups from useGroup now includes archived groups
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [editingGroupId, setEditingGroupId] = useState(null);
    const [editingGroupName, setEditingGroupName] = useState('');
    const [editingGroup, setEditingGroup] = useState(null);
    const [showArchived, setShowArchived] = useState(false);

    const handleCreateGroup = async (e) => {
        e.preventDefault();
        if (!newGroupName.trim()) return;

        try {
            await createGroup(newGroupName.trim(), csrfToken);
            setNewGroupName('');
            setShowCreateForm(false);
            await refreshGroups();
        } catch (error) {
            alert('Error creating group: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleStartEdit = (group) => {
        setShowCreateForm(false); // Collapse create form if open
        // Only collapse archived groups if editing a non-archived group
        if (!group.archived_at) {
            setShowArchived(false);
        }
        setEditingGroupId(group.id);
        setEditingGroupName(group.name);
        setEditingGroup(group);
    };

    const handleCancelEdit = () => {
        setEditingGroupId(null);
        setEditingGroupName('');
        setEditingGroup(null);
    };

    const handleUpdateGroup = async (e) => {
        e.preventDefault();
        if (!editingGroupName.trim()) return;

        try {
            await updateGroup(editingGroupId, editingGroupName.trim(), csrfToken);
            setEditingGroupId(null);
            setEditingGroupName('');
            setEditingGroup(null);
            await refreshGroups();
        } catch (error) {
            alert('Error updating group: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleToggleFavorite = async (group) => {
        if (!group?.id || group.is_default) return;
        const isFavorite = favoriteGroupId === group.id;
        try {
            await setGroupFavorite(group.id, !isFavorite, csrfToken);
            await refreshGroups();
        } catch (err) {
            alert('Error updating favorite: ' + (err.response?.data?.error || err.message));
        }
    };

    const handleArchiveGroup = async () => {
        if (!editingGroup) return;
        
        const isArchived = editingGroup.archived_at != null && editingGroup.archived_at !== '';
        const action = isArchived ? 'unarchive' : 'archive';
        
        if (!window.confirm(`Are you sure you want to ${action} "${editingGroup.name}"? This will not affect the bills in this group.`)) {
            return;
        }

        try {
            const result = await archiveGroup(editingGroup.id, csrfToken);
            // Refresh groups to get updated archive status
            await refreshGroups();
            // Clear editing state after successful archive/unarchive
            setEditingGroupId(null);
            setEditingGroupName('');
            setEditingGroup(null);
            // If unarchiving, ensure archived groups are visible so user can see the change
            if (isArchived) {
                setShowArchived(true);
            }
        } catch (error) {
            alert('Error archiving group: ' + (error.response?.data?.error || error.message));
        }
    };

    if (loading) {
        return (
            <Box>
                <Typography variant="body1" sx={{ color: theme.colors.text }}>
                    Loading groups...
                </Typography>
            </Box>
        );
    }

    // Filter groups: always show non-archived, optionally show archived
    const nonArchivedGroups = (groups || []).filter(group => !group.archived_at);
    const archivedGroups = showArchived ? (groups || []).filter(group => group.archived_at) : [];
    
    // Sort non-archived groups: non-default first, then default at the bottom
    const sortedNonArchived = [...nonArchivedGroups].sort((a, b) => {
        if (a.is_default && !b.is_default) return 1;
        if (!a.is_default && b.is_default) return -1;
        return 0;
    });
    
    // Sort archived groups
    const sortedArchived = [...archivedGroups].sort((a, b) => {
        if (a.is_default && !b.is_default) return 1;
        if (!a.is_default && b.is_default) return -1;
        return 0;
    });
    
    // Check if there are any archived groups
    const hasArchivedGroups = (groups || []).some(group => group.archived_at);
    

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: 'none',
                borderBottom: 'none',
                mb: '40px',
                '& .MuiTableContainer-root': {
                    borderBottom: 'none !important',
                    '&::after': {
                        display: 'none !important',
                    },
                },
            }}
        >
            {(groups && groups.length > 0) || showCreateForm ? (
                <TableContainer
                    component={Box}
                    sx={{
                        mb: 0,
                        pb: 0,
                        mt: 0,
                        pt: 0,
                        mr: 0,
                        ml: 0,
                        width: '100%',
                        borderRadius: 2,
                        backgroundColor: theme.colors.tableBackground || theme.colors.background,
                        border: 'none !important',
                        borderBottom: 'none !important',
                        borderTop: 'none !important',
                        boxShadow: theme.shadows?.table || '0 4px 12px rgba(0, 0, 0, 0.15)',
                        outline: 'none !important',
                        overflow: 'hidden',
                        '&::before': {
                            display: 'none !important',
                        },
                        '&::after': {
                            display: 'none !important',
                        },
                        '&.MuiPaper-root': {
                            border: 'none !important',
                            borderBottom: 'none !important',
                            borderTop: 'none !important',
                            boxShadow: theme.shadows?.table || '0 4px 12px rgba(0, 0, 0, 0.15)',
                            marginBottom: '0 !important',
                            paddingBottom: '0 !important',
                            outline: 'none !important',
                            borderRadius: 2,
                        },
                        '& .MuiPaper-root': {
                            border: 'none !important',
                            borderBottom: 'none !important',
                            borderTop: 'none !important',
                            boxShadow: theme.shadows?.table || '0 4px 12px rgba(0, 0, 0, 0.15)',
                            marginBottom: '0 !important',
                            paddingBottom: '0 !important',
                            outline: 'none !important',
                            borderRadius: 2,
                        },
                        '& .MuiTable-root': {
                            border: 'none !important',
                            borderBottom: 'none !important',
                            borderTop: 'none !important',
                            marginBottom: '0 !important',
                            '& .MuiTableBody-root': {
                                border: 'none !important',
                                borderBottom: 'none !important',
                                '& .MuiTableRow-root:last-child .MuiTableCell-root': {
                                    borderBottom: 'none !important',
                                },
                            },
                        },
                    }}
                >
                    <Table sx={{
                        border: 'none !important',
                        borderBottom: 'none !important',
                        borderCollapse: 'collapse',
                        borderSpacing: 0,
                        width: '100%',
                        tableLayout: 'auto',
                    }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                <TableCell sx={{
                                    color: theme.colors.brandSecondary,
                                    fontWeight: 700,
                                    pl: 0,
                                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                                    py: 2,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: 0,
                                }}>Groups</TableCell>
                                <TableCell sx={{
                                    width: '3.5rem !important',
                                    minWidth: '3.5rem !important',
                                    maxWidth: '3.5rem !important',
                                    p: 0,
                                    flexShrink: 0,
                                    flexGrow: 0,
                                }}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* Non-Archived Group Rows */}
                            {sortedNonArchived && sortedNonArchived.map((group) => (
                                <React.Fragment key={group.id}>
                                    {/* Header Row (when editing) */}
                                    {editingGroupId === group.id && (
                                        <TableRow>
                                            <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0, borderBottom: 'none' }}></TableCell>
                                            <TableCell colSpan={1} sx={{ pl: 0, pr: 0, py: 1, borderBottom: 'none' }}>
                                                <Typography variant="h6" sx={{ color: theme.colors.text, fontWeight: 600 }}>
                                                    Edit Group
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ 
                                                width: '3.5rem !important', 
                                                minWidth: '3.5rem !important', 
                                                maxWidth: '3.5rem !important', 
                                                p: 0,
                                                flexShrink: 0,
                                                flexGrow: 0,
                                                borderBottom: 'none',
                                            }}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                    height: '100%'
                                                }}>
                                                    <IconButton
                                                        onClick={handleCancelEdit}
                                                        size="small"
                                                        sx={{
                                                            color: 'inherit',
                                                            padding: '4px',
                                                            '&:hover': {
                                                                backgroundColor: theme.colors.overlayHover,
                                                            },
                                                        }}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    
                                    {/* Form Row (when editing) */}
                                    {editingGroupId === group.id && (
                                        <TableRow>
                                            <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                            <TableCell colSpan={1} sx={{ pl: 0, pr: 0, py: 2 }}>
                                                <Box
                                                    component="form"
                                                    onSubmit={handleUpdateGroup}
                                                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                                                >
                                                    <InputValidatorField
                                                        helperText="(Required, max 50 characters)"
                                                        label="Group Name"
                                                        name="name"
                                                        value={editingGroupName}
                                                        handleChange={(e) => setEditingGroupName(e.target.value)}
                                                        isValidInput={IsValidName}
                                                        requirementsText="Required - provide a name for the group (max 50 characters)"
                                                    />
                                                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                                                        {!editingGroup?.is_default && (
                                                            <Button
                                                                type="button"
                                                                onClick={handleArchiveGroup}
                                                                variant="outlined"
                                                                fullWidth
                                                                sx={{
                                                                    border: `1px solid ${editingGroup?.archived_at ? theme.colors.success || '#4caf50' : theme.colors.warning || '#ff9800'}`,
                                                                    backgroundColor: 'transparent',
                                                                    color: editingGroup?.archived_at ? theme.colors.success || '#4caf50' : theme.colors.warning || '#ff9800',
                                                                    '&:hover': {
                                                                        borderColor: editingGroup?.archived_at ? theme.colors.success || '#4caf50' : theme.colors.warning || '#ff9800',
                                                                        backgroundColor: editingGroup?.archived_at 
                                                                            ? (theme.colors.successHover || 'rgba(76, 175, 80, 0.1)')
                                                                            : (theme.colors.warningHover || 'rgba(255, 152, 0, 0.1)'),
                                                                    },
                                                                }}
                                                            >
                                                                {editingGroup?.archived_at ? 'Unarchive' : 'Archive'}
                                                            </Button>
                                                        )}
                                                        {(!editingGroupName.trim() || editingGroupName.trim() === editingGroup?.name || !IsValidName(editingGroupName)) ? (
                                                            <Button
                                                                type="button"
                                                                onClick={handleCancelEdit}
                                                                variant="outlined"
                                                                fullWidth
                                                                sx={{
                                                                    border: `1px solid ${theme.colors.border || '#ccc'}`,
                                                                    backgroundColor: 'transparent',
                                                                    color: theme.colors.text,
                                                                    '&:hover': {
                                                                        borderColor: theme.colors.border || '#ccc',
                                                                        backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                                    },
                                                                }}
                                                            >
                                                                Cancel
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                type="submit"
                                                                variant="outlined"
                                                                fullWidth
                                                                sx={{
                                                                    border: `1px solid ${theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary}`,
                                                                    backgroundColor: 'transparent',
                                                                    color: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                                                    '&:hover': {
                                                                        borderColor: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                                                        backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                                    },
                                                                }}
                                                            >
                                                                Save
                                                            </Button>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                        </TableRow>
                                    )}
                                    
                                    {/* Normal Row (when not editing) */}
                                    {editingGroupId !== group.id && (
                                    <TableRow>
                                        <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}>
                                            {!group.is_default && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleToggleFavorite(group)}
                                                    sx={{
                                                        color: favoriteGroupId === group.id ? (theme.colors.brandSecondary || theme.colors.warning) : theme.colors.text,
                                                        padding: '4px',
                                                        '&:hover': { backgroundColor: theme.colors.overlayHover },
                                                    }}
                                                    aria-label={favoriteGroupId === group.id ? 'Unfavorite group' : 'Favorite group'}
                                                >
                                                    {favoriteGroupId === group.id ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                                                </IconButton>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{
                                            color: theme.colors.text,
                                            pl: 0,
                                            fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: 0,
                                            fontWeight: 'bold',
                                        }}>
                                            {group.name}
                                            {!group.is_default && group.archived_at && (
                                                    <Chip
                                                        label="Archived"
                                                        size="small"
                                                        sx={{
                                                            ml: 1,
                                                            backgroundColor: theme.colors.warning || '#ff9800',
                                                            color: 'white',
                                                            fontSize: '0.75rem',
                                                        }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell sx={{
                                                width: '3.5rem !important',
                                                minWidth: '3.5rem !important',
                                                maxWidth: '3.5rem !important',
                                                p: 0,
                                                flexShrink: 0,
                                                flexGrow: 0,
                                            }}>
                                                {!group.is_default && (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        width: '100%',
                                                    }}>
                                                    <IconButton
                                                        onClick={() => handleStartEdit(group)}
                                                        size="small"
                                                        sx={{
                                                            color: theme.colors.text,
                                                            padding: '4px',
                                                            '&:hover': {
                                                                backgroundColor: theme.colors.overlayHover,
                                                            },
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            )}
                                        </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))}

                            {/* Expand/Collapse Archived Groups Row */}
                            {hasArchivedGroups && !showCreateForm && (
                                <TableRow>
                                    <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                    <TableCell sx={{ pl: 0, pr: 0, py: 1, maxWidth: 0 }}>
                                        <Button
                                            variant="text"
                                            onClick={() => setShowArchived(!showArchived)}
                                            startIcon={showArchived ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            fullWidth
                                            sx={{
                                                color: theme.colors.text,
                                                justifyContent: 'flex-start',
                                                textTransform: 'none',
                                                '&:hover': {
                                                    backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                },
                                            }}
                                        >
                                            {showArchived ? 'Hide Archived Groups' : 'Show Archived Groups'}
                                        </Button>
                                    </TableCell>
                                    <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                </TableRow>
                            )}

                            {/* Archived Group Rows */}
                            {sortedArchived && sortedArchived.map((group) => (
                                <React.Fragment key={group.id}>
                                    {/* Header Row (when editing) */}
                                    {editingGroupId === group.id && (
                                        <TableRow>
                                            <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0, borderBottom: 'none' }}></TableCell>
                                            <TableCell colSpan={1} sx={{ pl: 0, pr: 0, py: 1, borderBottom: 'none' }}>
                                                <Typography variant="h6" sx={{ color: theme.colors.text, fontWeight: 600 }}>
                                                    Edit Group
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ 
                                                width: '3.5rem !important', 
                                                minWidth: '3.5rem !important', 
                                                maxWidth: '3.5rem !important', 
                                                p: 0,
                                                flexShrink: 0,
                                                flexGrow: 0,
                                                borderBottom: 'none',
                                            }}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                    height: '100%'
                                                }}>
                                                    <IconButton
                                                        onClick={handleCancelEdit}
                                                        size="small"
                                                        sx={{
                                                            color: 'inherit',
                                                            padding: '4px',
                                                            '&:hover': {
                                                                backgroundColor: theme.colors.overlayHover,
                                                            },
                                                        }}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    
                                    {/* Form Row (when editing) */}
                                    {editingGroupId === group.id && (
                                        <TableRow>
                                            <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                            <TableCell colSpan={1} sx={{ pl: 0, pr: 0, py: 2 }}>
                                                <Box
                                                    component="form"
                                                    onSubmit={handleUpdateGroup}
                                                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                                                >
                                                    <InputValidatorField
                                                        helperText="(Required, max 50 characters)"
                                                        label="Group Name"
                                                        name="name"
                                                        value={editingGroupName}
                                                        handleChange={(e) => setEditingGroupName(e.target.value)}
                                                        isValidInput={IsValidName}
                                                        requirementsText="Required - provide a name for the group (max 50 characters)"
                                                    />
                                                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                                                        {!editingGroup?.is_default && (
                                                            <Button
                                                                type="button"
                                                                onClick={handleArchiveGroup}
                                                                variant="outlined"
                                                                fullWidth
                                                                sx={{
                                                                    border: `1px solid ${editingGroup?.archived_at ? theme.colors.success || '#4caf50' : theme.colors.warning || '#ff9800'}`,
                                                                    backgroundColor: 'transparent',
                                                                    color: editingGroup?.archived_at ? theme.colors.success || '#4caf50' : theme.colors.warning || '#ff9800',
                                                                    '&:hover': {
                                                                        borderColor: editingGroup?.archived_at ? theme.colors.success || '#4caf50' : theme.colors.warning || '#ff9800',
                                                                        backgroundColor: editingGroup?.archived_at 
                                                                            ? (theme.colors.successHover || 'rgba(76, 175, 80, 0.1)')
                                                                            : (theme.colors.warningHover || 'rgba(255, 152, 0, 0.1)'),
                                                                    },
                                                                }}
                                                            >
                                                                {editingGroup?.archived_at ? 'Unarchive' : 'Archive'}
                                                            </Button>
                                                        )}
                                                        {(!editingGroupName.trim() || editingGroupName.trim() === editingGroup?.name || !IsValidName(editingGroupName)) ? (
                                                            <Button
                                                                type="button"
                                                                onClick={handleCancelEdit}
                                                                variant="outlined"
                                                                fullWidth
                                                                sx={{
                                                                    border: `1px solid ${theme.colors.border || '#ccc'}`,
                                                                    backgroundColor: 'transparent',
                                                                    color: theme.colors.text,
                                                                    '&:hover': {
                                                                        borderColor: theme.colors.border || '#ccc',
                                                                        backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                                    },
                                                                }}
                                                            >
                                                                Cancel
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                type="submit"
                                                                variant="outlined"
                                                                fullWidth
                                                                sx={{
                                                                    border: `1px solid ${theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary}`,
                                                                    backgroundColor: 'transparent',
                                                                    color: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                                                    '&:hover': {
                                                                        borderColor: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                                                        backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                                    },
                                                                }}
                                                            >
                                                                Save
                                                            </Button>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                        </TableRow>
                                    )}
                                    
                                    {/* Normal Row (when not editing) */}
                                    {editingGroupId !== group.id && (
                                    <TableRow>
                                        <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}>
                                            {!group.is_default && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleToggleFavorite(group)}
                                                    sx={{
                                                        color: favoriteGroupId === group.id ? (theme.colors.brandSecondary || theme.colors.warning) : theme.colors.text,
                                                        padding: '4px',
                                                        opacity: 0.8,
                                                        '&:hover': { backgroundColor: theme.colors.overlayHover },
                                                    }}
                                                    aria-label={favoriteGroupId === group.id ? 'Unfavorite group' : 'Favorite group'}
                                                >
                                                    {favoriteGroupId === group.id ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                                                </IconButton>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{
                                            color: theme.colors.text,
                                            pl: 0,
                                            fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: 0,
                                            fontWeight: 'bold',
                                            opacity: 0.7,
                                        }}>
                                                {group.name}
                                                <Chip
                                                    label="Archived"
                                                    size="small"
                                                    sx={{
                                                        ml: 1,
                                                        backgroundColor: theme.colors.warning || '#ff9800',
                                                        color: 'white',
                                                        fontSize: '0.75rem',
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{
                                                width: '3.5rem !important',
                                                minWidth: '3.5rem !important',
                                                maxWidth: '3.5rem !important',
                                                p: 0,
                                                flexShrink: 0,
                                                flexGrow: 0,
                                            }}>
                                                {!group.is_default && (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        width: '100%',
                                                    }}>
                                                        <IconButton
                                                            onClick={() => handleStartEdit(group)}
                                                            size="small"
                                                            sx={{
                                                                color: theme.colors.text,
                                                                padding: '4px',
                                                                '&:hover': {
                                                                    backgroundColor: theme.colors.overlayHover,
                                                                },
                                                            }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))}

                            {/* Header Row (when creating) */}
                            {showCreateForm && (
                                <TableRow>
                                    <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0, borderBottom: 'none' }}></TableCell>
                                    <TableCell colSpan={1} sx={{ pl: 0, pr: 0, py: 1, borderBottom: 'none' }}>
                                        <Typography variant="h6" sx={{ color: theme.colors.text, fontWeight: 600 }}>
                                            Create Group
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ 
                                        width: '3.5rem !important', 
                                        minWidth: '3.5rem !important', 
                                        maxWidth: '3.5rem !important', 
                                        p: 0,
                                        flexShrink: 0,
                                        flexGrow: 0,
                                        borderBottom: 'none',
                                    }}>
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            width: '100%',
                                            height: '100%'
                                        }}>
                                            <IconButton
                                                onClick={() => {
                                                    setShowCreateForm(false);
                                                    setNewGroupName('');
                                                }}
                                                size="small"
                                                sx={{
                                                    color: 'inherit',
                                                    padding: '4px',
                                                    '&:hover': {
                                                        backgroundColor: theme.colors.overlayHover,
                                                    },
                                                }}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                            
                            {/* Form Row (when creating) */}
                            {showCreateForm && (
                                <TableRow>
                                    <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                    <TableCell colSpan={1} sx={{ pl: 0, pr: 0, py: 2 }}>
                                        <Box
                                            component="form"
                                            onSubmit={handleCreateGroup}
                                            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                                        >
                                            <InputValidatorField
                                                helperText="(Required, max 50 characters)"
                                                label="Group Name"
                                                name="name"
                                                value={newGroupName}
                                                handleChange={(e) => setNewGroupName(e.target.value)}
                                                isValidInput={IsValidName}
                                                requirementsText="Required - provide a name for the group (max 50 characters)"
                                            />
                                            {(!newGroupName.trim() || !IsValidName(newGroupName)) ? (
                                                <Button
                                                    type="button"
                                                    onClick={() => {
                                                        setShowCreateForm(false);
                                                        setNewGroupName('');
                                                    }}
                                                    variant="outlined"
                                                    fullWidth
                                                    sx={{
                                                        border: `1px solid ${theme.colors.border || '#ccc'}`,
                                                        backgroundColor: 'transparent',
                                                        color: theme.colors.text,
                                                        '&:hover': {
                                                            borderColor: theme.colors.border || '#ccc',
                                                            backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                        },
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                            ) : (
                                                <Button
                                                    type="submit"
                                                    variant="outlined"
                                                    fullWidth
                                                    sx={{
                                                        border: `1px solid ${theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary}`,
                                                        backgroundColor: 'transparent',
                                                        color: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                                        '&:hover': {
                                                            borderColor: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                                            backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                        },
                                                    }}
                                                >
                                                    Create
                                                </Button>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                </TableRow>
                            )}

                            {/* Create Group Button Row */}
                            {!showCreateForm && !editingGroupId && (
                                <TableRow>
                                    <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                    <TableCell sx={{ pl: 0, pr: 0, py: 1, maxWidth: 0 }}>
                                        <Button
                                            variant="outlined"
                                            onClick={() => {
                                                setEditingGroupId(null); // Collapse any edit form if open
                                                setEditingGroupName('');
                                                setEditingGroup(null);
                                                setShowArchived(false); // Collapse archived groups
                                                setShowCreateForm(true);
                                            }}
                                            startIcon={<AddIcon />}
                                            fullWidth
                                            sx={{
                                                borderColor: theme.current === 'light'
                                                    ? theme.colors.brandSecondary
                                                    : (theme.colors.secondary || theme.colors.brandSecondary),
                                                color: theme.current === 'light' 
                                                    ? '#000000' 
                                                    : (theme.colors.secondary || theme.colors.brandSecondary),
                                                backgroundColor: theme.current === 'light'
                                                    ? theme.colors.brandSecondary
                                                    : (theme.colors.tableBackground || theme.colors.background),
                                                '&:hover': {
                                                    borderColor: theme.current === 'light'
                                                        ? theme.colors.brandSecondary
                                                        : (theme.colors.secondary || theme.colors.brandSecondary),
                                                    backgroundColor: theme.current === 'light'
                                                        ? theme.colors.brandSecondary
                                                        : (theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)'),
                                                },
                                            }}
                                        >
                                            Create Group
                                        </Button>
                                    </TableCell>
                                    <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Typography variant="body1" sx={{ color: theme.colors.text, p: 2, fontStyle: 'italic' }}>
                    No groups yet. Create your first group!
                </Typography>
            )}
        </Box>
    );
};

export default GroupList;
